import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaEye,
  FaEyeSlash,
  FaHeading,
  FaParagraph,
  FaSave,
  FaToggleOn,
} from "react-icons/fa";

const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

function SectionCard({ eyebrow, title, icon, description, children }) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            {eyebrow}
          </p>

          <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
            <span className="text-indigo-600 dark:text-indigo-300">
              {icon}
            </span>
            {title}
          </h3>

          {description ? (
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {description}
            </p>
          ) : null}
        </div>

        {children}
      </div>
    </section>
  );
}

function SkeletonForm() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-5">
        {[...Array(6)].map((_, index) => (
          <div
            key={index}
            className="h-14 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

export default function InfoClubForm({
  formId,
  settings,
  loading,
  saving,
  onChange,
  onContentChange,
  onToggleActive,
  onSubmit,
}) {
  if (loading) return <SkeletonForm />;

  return (
    <form id={formId} onSubmit={onSubmit} className="space-y-5">
      <SectionCard
        eyebrow="Visibilité"
        title="Activation de l’encart"
        icon={<FaToggleOn />}
        description="Contrôle l’affichage de la section Info Club sur la page d’accueil."
      >
        <div className="flex flex-col gap-4 rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
              {settings.infoclub_isactive ? (
                <>
                  <FaEye className="text-emerald-500" />
                  Section active
                </>
              ) : (
                <>
                  <FaEyeSlash className="text-slate-400" />
                  Section masquée
                </>
              )}
            </p>

            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Lorsque la section est désactivée, elle n’apparaît pas sur la page
              d’accueil, même si le titre et le contenu sont renseignés.
            </p>
          </div>

          <button
            type="button"
            onClick={onToggleActive}
            className={`flex h-8 w-14 shrink-0 items-center rounded-full p-1 transition ${
              settings.infoclub_isactive
                ? "bg-emerald-500"
                : "bg-slate-300 dark:bg-slate-700"
            }`}
            title={settings.infoclub_isactive ? "Désactiver" : "Activer"}
          >
            <span
              className={`h-6 w-6 rounded-full bg-white shadow transition ${
                settings.infoclub_isactive ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>
      </SectionCard>

      <SectionCard
        eyebrow="Contenu"
        title="Titre de la section"
        icon={<FaHeading />}
        description="Titre affiché en haut de l’encart Info Club."
      >
        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
          Titre
        </label>

        <input
          type="text"
          name="infoclub_title"
          value={settings.infoclub_title || ""}
          onChange={onChange}
          placeholder="Ex : Informations du club"
          className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
        />
      </SectionCard>

      <SectionCard
        eyebrow="Message"
        title="Contenu de l’encart"
        icon={<FaParagraph />}
        description="Ajoutez vos informations importantes : horaires, événements, rappels, consignes ou messages aux adhérents."
      >
        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
          Contenu
        </label>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 quill-wrapper">
          <ReactQuill
            value={settings.infoclub_content || ""}
            onChange={(value) =>
              onContentChange("infoclub_content", value)
            }
            modules={modules}
            formats={formats}
            placeholder="<p>Votre message important ici…</p>"
          />
        </div>

        <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
          Astuce : utilisez le gras pour mettre en avant les horaires, dates
          importantes ou consignes prioritaires.
        </p>
      </SectionCard>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
        >
          <FaSave />
          {saving ? "Enregistrement..." : "Mettre à jour"}
        </button>
      </div>

      <style>{`
        .quill-wrapper .ql-toolbar.ql-snow {
          border: 0;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
        }

        .quill-wrapper .ql-container.ql-snow {
          border: 0;
          min-height: 220px;
        }

        .quill-wrapper .ql-editor {
          min-height: 220px;
          font-size: 14px;
          line-height: 1.7;
        }

        .dark .quill-wrapper .ql-toolbar.ql-snow {
          background: rgba(15,23,42,.98);
          border-color: #334155;
        }

        .dark .quill-wrapper .ql-container.ql-snow {
          background: rgba(2,6,23,.7);
        }

        .dark .quill-wrapper .ql-editor {
          color: #e2e8f0;
        }

        .dark .ql-snow .ql-stroke {
          stroke: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-fill {
          fill: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-picker,
        .dark .ql-snow .ql-picker-label,
        .dark .ql-snow .ql-picker-options,
        .dark .ql-snow .ql-toolbar button {
          color: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-picker-options {
          background: #0b1220;
          border-color: #334155;
        }

        input::placeholder {
          color: #94a3b8;
        }

        .dark input::placeholder {
          color: #94a3b8;
          opacity: .7;
        }
      `}</style>
    </form>
  );
}